import React from 'react';
import ContentPageTemplate from '@/components/ContentPageTemplate';
import { Button } from '@/components/ui/button';
import SchemaMarkup from '@/components/SchemaMarkup';
import { buildFAQSchema } from '@/lib/schema';

const FAQ_ITEMS = [
  {
    question: 'Do I need a permit for impact windows in Broward County?',
    answer: 'Yes. All impact window installations in Broward County require a building permit. We handle the entire permit process, including application, submission, and inspection coordination.',
  },
  {
    question: 'How long does installation take in Fort Lauderdale?',
    answer: 'The entire process from contract to completion takes 8–12 weeks. Physical installation typically takes 1–3 days. Most of the timeline is permit approval (5–10 days) and product manufacturing (4–10 weeks).',
  },
  {
    question: 'Can I replace windows in a waterfront condo in Fort Lauderdale?',
    answer: 'Yes. Waterfront condo projects typically require board approval and may have specific product or finish requirements. We handle all HOA coordination and building compliance. Expect to add 2–4 weeks for HOA approval.',
  },
  {
    question: 'Will impact windows lower my insurance in Fort Lauderdale?',
    answer: "Yes. Most homeowners see 15–45% premium reductions after completing a wind mitigation inspection, with higher savings for waterfront properties. You'll need the permit sign-off and installation certificates to claim the discount.",
  },
  {
    question: 'What affects the price of impact windows in Fort Lauderdale?',
    answer: 'Price depends on: (1) Product tier (Good/Better/Premium), (2) Number and size of openings, (3) Waterfront or coastal property requirements, (4) HOA or building requirements, and (5) Installation complexity (high-rise vs single-family).',
  },
];

export default function ImpactWindowsFortLauderdale() {
  const pageData = {
    title: "Impact Windows in Fort Lauderdale: Pricing, Permits, and Installation",
    metaDescription: "Impact windows in Fort Lauderdale: real pricing, permit requirements, and installation timelines. Get a photo-based quote — no in-home appointment.",
    canonicalPath: "/impact-windows-fort-lauderdale",
    
    directAnswer: (
      <>
        <p>
          Impact window installation in Fort Lauderdale and Broward County requires <strong>Broward County Product Approval</strong>, building permits, and High Velocity Hurricane Zone (HVHZ) compliance. Most residential projects cost <strong>$15,000–$40,000</strong> depending on property size and product tier. Installation timelines average <strong>8–12 weeks</strong> from contract to completion.
        </p>
      </>
    ),

    sections: [
      {
        title: "Why Fort Lauderdale Homeowners Choose Impact Windows",
        testId: "why-fort-lauderdale",
        content: (
          <p className="mb-4">
            Fort Lauderdale's coastal exposure and extensive waterfront properties make impact windows critical for protection. From canal-side homes in Las Olas Isles to beachfront condos in Hollywood, impact windows are essential throughout Broward County:
          </p>
        ),
        bullets: [
          "Hurricane Protection: Broward County is in the HVHZ, requiring impact-rated products for coastal properties",
          "Waterfront Properties: Protect canal-side and beachfront homes from storm surge and wind-driven debris — especially critical for Intracoastal and Atlantic-facing properties",
          "Insurance Savings: Reduce premiums by 10–45% annually with wind mitigation inspection",
          "Noise Reduction: Block traffic noise from Las Olas, Federal Highway, A1A, and boat traffic along the Intracoastal",
          "Energy Efficiency: Reduce cooling costs in South Florida's year-round heat",
          "Condo Requirements: Many waterfront condo buildings in Fort Lauderdale Beach and Hollywood now require impact windows for all unit upgrades"
        ],
        highlight: "Broward County requires all impact products to carry Broward Product Approval. We only use certified products. Learn more about the installation process."
      },

      {
        title: "Broward County Permit Requirements",
        testId: "permits",
        content: (
          <div className="space-y-4">
            <p className="mb-4">
              All impact window installations in Broward County require building permits. For waterfront properties along the Intracoastal in Fort Lauderdale, we've handled dozens of projects requiring both county permits and HOA approvals.
            </p>
          </div>
        ),
        bullets: [
          "Permit Application: Includes product approvals, installation plans, and contractor verification",
          "Approval Timeline: 5–10 business days for standard residential projects",
          "Permit Fees: Typically $400–$800 depending on scope",
          "Inspection Required: County inspector verifies HVHZ compliance before final sign-off"
        ],
        highlight: "We handle all permit preparation and submission. You don't need to visit the building department. See our full installation timeline."
      },

      {
        title: "Pricing for Fort Lauderdale Homes",
        testId: "pricing",
        table: {
          headers: ["Property Type", "Typical Openings", "Estimated Cost Range"],
          rows: [
            ["Condo/Townhome", "8–12 windows", "$15,000 – $22,000"],
            ["Single-Family Home", "12–18 windows + 2–3 doors", "$20,000 – $35,000"],
            ["Waterfront Home", "18–25 windows + 3–4 doors", "$30,000 – $50,000"],
            ["Large Estate", "25+ windows + 4+ doors", "$50,000 – $100,000+"]
          ]
        },
        content: (
          <p className="mt-4">
            Pricing includes product, installation, permits, old window removal, and inspection coordination.
          </p>
        ),
        highlight: "We provide Good, Better, and Premium options so you can choose what fits your budget."
      },

      {
        title: "Areas We Serve in Broward County",
        testId: "areas-served",
        content: (
          <div className="mb-4">
            <p className="mb-4">We serve all of Broward County, including:</p>
            <div className="grid md:grid-cols-3 gap-3">
              <ul className="list-disc pl-6 space-y-1 text-sm text-[var(--brand-slate-700)]">
                <li>Fort Lauderdale</li>
                <li>Pembroke Pines</li>
                <li>Hollywood</li>
                <li>Miramar</li>
                <li>Plantation</li>
              </ul>
              <ul className="list-disc pl-6 space-y-1 text-sm text-[var(--brand-slate-700)]">
                <li>Davie</li>
                <li>Sunrise</li>
                <li>Coral Springs</li>
                <li>Pompano Beach</li>
                <li>Deerfield Beach</li>
              </ul>
              <ul className="list-disc pl-6 space-y-1 text-sm text-[var(--brand-slate-700)]">
                <li>Weston</li>
                <li>Hallandale Beach</li>
                <li>Lauderhill</li>
                <li>Tamarac</li>
                <li>Coconut Creek</li>
              </ul>
            </div>
          </div>
        )
      },

      {
        title: "Frequently Asked Questions",
        testId: "faq",
        content: (
          <div className="space-y-6">
            {FAQ_ITEMS.map(({ question, answer }, i) => (
              <div key={i}>
                <p className="font-semibold text-[var(--brand-slate-900)] mb-2">{question}</p>
                <p className="text-[var(--brand-slate-700)]">{answer}</p>
              </div>
            ))}
          </div>
        )
      }
    ],

    relatedLinks: [
      {
        title: "Get Your Quote",
        description: "Upload photos and receive pricing",
        url: "/get-quote?src=fort_lauderdale"
      },
      {
        title: "See Real Projects",
        description: "Fort Lauderdale case studies",
        url: "/case-studies"
      },
      {
        title: "Installation Process",
        description: "Step-by-step timeline",
        url: "/installation-process"
      },
      {
        title: "Insurance Savings",
        description: "Wind mitigation explained",
        url: "/insurance-savings"
      }
    ],

    ctaText: "Get My Price",
    ctaSubtext: "Upload photos and receive Good, Better, and Premium pricing options for your Fort Lauderdale home. No in-home appointment required. No pressure. Just clear pricing.",

    breadcrumbs: [
      { label: "Home", link: "/" },
      { label: "Impact Windows Fort Lauderdale" }
    ]
  };

  return (
    <>
      <SchemaMarkup schemas={buildFAQSchema(FAQ_ITEMS)} instanceId="impact-windows-fort-lauderdale-faq" />
      <ContentPageTemplate {...pageData} />
    </>
  );
}
